import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Skeleton } from "../../components/ui/skeleton";

import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";

import { formatDateTime } from "../../lib/format";
import { useForexClients, useForexClientsSummary } from "./api";
import {
  FilterBar,
  FilterField,
  Money,
  PAGE_SIZE_OPTIONS,
  PagePager,
  historyUrl,
  orDash,
} from "./parts";

const EMPTY = { login: "", username: "", from: "", to: "", pageSize: "50" };

// datetime-local is the admin's local time; the API wants UTC.
const toUtc = (v) => (v ? new Date(v).toISOString() : "");

const mono = "whitespace-nowrap font-mono";

// Balances and margin are the state now; only realised, trades and lots follow
// the date window. floatingPnl and realisedProfit are never summed.
const FIGURES = [
  { key: "balance", label: "Balance", money: true },
  { key: "credit", label: "Credit", money: true },
  { key: "equity", label: "Equity", money: true },
  { key: "margin", label: "Margin", money: true },
  { key: "freeMargin", label: "Free margin", money: true },
  { key: "floatingPnl", label: "Floating P/L", money: true },
  { key: "realisedProfit", label: "Realised", money: true },
  { key: "lots", label: "Lots" },
  { key: "openLots", label: "Open lots" },
  { key: "trades", label: "Trades" },
  { key: "openPositions", label: "Open positions" },
];

const COLUMNS = [
  { label: "" },
  { label: "Holder" },
  { label: "Username" },
  { label: "Logins" },
  { label: "Accounts", align: "right" },
  { label: "Tier" },
  { label: "Purpose" },
  { label: "Unattributed" },
  ...FIGURES.map((f) => ({ label: f.label, align: "right" })),
];

const link = (field, value) => (
  <Link to={historyUrl(field, value)} className="hover:text-primary hover:underline">
    {value}
  </Link>
);

const Figures = ({ row }) =>
  FIGURES.map((f) => (
    <Td key={f.key} align="right" className={mono}>
      {f.money ? <Money value={row[f.key]} /> : row[f.key]}
    </Td>
  ));

// null tier with several tiers held is "mixed", not "unknown".
const tierLabel = (row) =>
  row.tier ?? (row.tiers?.length ? `Mixed: ${row.tiers.join(", ")}` : orDash(null));

const unattributedCell = (flag) =>
  flag ? (
    <span className="inline-flex rounded-full border border-amber-500/20 bg-amber-500/10 px-1.5 py-px text-[10px] font-medium text-amber-400">
      Yes
    </span>
  ) : (
    "No"
  );

function ClientRows({ row, open, onToggle }) {
  return (
    <>
      <tr onClick={onToggle} className="cursor-pointer hover:bg-white/[0.02]">
        <Td className="text-muted-foreground">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </Td>
        <Td className="whitespace-nowrap font-medium">{orDash(row.holderName)}</Td>
        <Td className="font-mono">{row.username ? link("username", row.username) : orDash(null)}</Td>
        <Td className={mono}>
          {row.logins.map((l, i) => (
            <Fragment key={l}>
              {i > 0 && ", "}
              {link("login", l)}
            </Fragment>
          ))}
        </Td>
        <Td align="right" className={mono}>{row.accountCount}</Td>
        <Td className="whitespace-nowrap text-xs">{tierLabel(row)}</Td>
        <Td className="whitespace-nowrap text-xs">{orDash(row.purpose)}</Td>
        <Td className="text-xs">{unattributedCell(row.unattributed)}</Td>
        <Figures row={row} />
      </tr>
      {/* Per account: free margin cannot be pooled, so "can they open this?"
          is answered here, never by the client total. */}
      {open &&
        row.accounts.map((a) => (
          <tr key={a.login} className="bg-white/[0.015]">
            <Td />
            <Td className="whitespace-nowrap text-xs text-muted-foreground">{orDash(a.holderName)}</Td>
            <Td className="font-mono text-xs">{a.username ? link("username", a.username) : orDash(null)}</Td>
            <Td className="font-mono">{link("login", a.login)}</Td>
            <Td />
            <Td className="text-xs">{orDash(a.tier)}</Td>
            <Td className="whitespace-nowrap text-xs">{orDash(a.purpose)}</Td>
            <Td className="text-xs">{unattributedCell(a.unattributed)}</Td>
            <Figures row={a} />
          </tr>
        ))}
    </>
  );
}

function Tile({ label, children }) {
  return (
    <div className="rounded-md border border-border/60 px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono text-lg">
        {children ?? <Skeleton className="mt-1.5 h-6 w-16" />}
      </div>
    </div>
  );
}

// Whole selection, not the page. inProfit + inLoss never add up to clients (a
// client with nothing realised is in neither), so they stay separate figures.
function Summary({ s }) {
  const t = s?.totals;
  const counts = [
    ["Clients", s?.clients],
    ["Accounts", s?.accounts],
    ["With open positions", s?.withOpenPositions],
    ["In profit", s?.inProfit],
    ["In loss", s?.inLoss],
    ["Unattributed accounts", s?.unattributedAccounts],
  ];
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {counts.map(([label, v]) => (
          <Tile key={label} label={label}>{v}</Tile>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {FIGURES.map((f) => (
          <Tile key={f.key} label={f.label}>
            {t && (f.money ? <Money value={t[f.key]} /> : t[f.key])}
          </Tile>
        ))}
      </div>
      {/* Grouped by the account's tier: clients can sum past the total, the
          money and accounts cannot. */}
      {s?.byTier?.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-3">
          {s.byTier.map((b) => (
            <div key={b.tier} className="space-y-1 rounded-md border border-border/60 px-4 py-3 text-xs">
              <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">{b.tier}</div>
              <div className="flex justify-between"><span className="text-muted-foreground">Accounts / clients</span><span className="font-mono">{b.accounts} / {b.clients}</span></div>
              {FIGURES.map((f) => (
                <div key={f.key} className="flex justify-between">
                  <span className="text-muted-foreground">{f.label}</span>
                  {f.money ? <Money value={b[f.key]} /> : <span className="font-mono">{b[f.key]}</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Login and username are both six digits: an empty result names the value
// that matched nothing rather than reading as "holds nothing".
const unmatchedText = (u) => {
  const parts = [
    u.login.length && `MT5 login ${u.login.join(", ")}`,
    u.username.length && `username ${u.username.join(", ")}`,
  ].filter(Boolean);
  return parts.length
    ? `No real-money account matched ${parts.join(" or ")}. Check the value is in the right field.`
    : "";
};

// The window the server applied, echoed back.
const windowText = (w) =>
  w?.from || w?.to
    ? `Activity window (UTC): ${w.from ? formatDateTime(w.from) : "start"} to ${w.to ? formatDateTime(w.to) : "now"}`
    : "Activity window: all time";

export default function Clients() {
  const [draft, setDraft] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(() => new Set());

  const filters = {
    login: applied.login.trim(),
    username: applied.username.trim(),
    from: toUtc(applied.from),
    to: toUtc(applied.to),
  };

  const list = useForexClients({ ...filters, pageSize: applied.pageSize, page });
  const summary = useForexClientsSummary(filters);
  const data = list.data;

  const toggle = (key) =>
    setOpen((s) => {
      const next = new Set(s);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const apply = () => {
    setApplied(draft);
    setPage(1);
  };

  const reset = () => {
    setDraft(EMPTY);
    setApplied(EMPTY);
    setPage(1);
  };

  // Same selection on both calls; the list's echo is used, the summary's when
  // the list failed.
  const echo = data ?? summary.data;
  const missing = echo ? unmatchedText(echo.unmatched) : "";

  return (
    <QueueLayout
      title="Client"
      accent="book"
      description="Every client's real-money accounts summed, largest equity first. Balances and margin are as of now; the dates narrow realised profit, trades and lots only."
      isFetching={list.isFetching || summary.isFetching}
      onRefresh={() => {
        list.refetch();
        summary.refetch();
      }}
    >
      <FilterBar onApply={apply} onReset={reset}>
        <FilterField label="MT5 login(s)" name="login" draft={draft} setDraft={setDraft} placeholder="261077, 101895" />
        <FilterField label="Username(s)" name="username" draft={draft} setDraft={setDraft} placeholder="217831" />
        <FilterField label="Activity from" name="from" type="datetime-local" draft={draft} setDraft={setDraft} />
        <FilterField label="Activity to" name="to" type="datetime-local" draft={draft} setDraft={setDraft} />
        <FilterField label="Page size" name="pageSize" options={PAGE_SIZE_OPTIONS} draft={draft} setDraft={setDraft} />
      </FilterBar>

      {/* A 503 is never an empty book: it stays an error state. */}
      {summary.error ? (
        <ErrorBanner error={summary.error} onRetry={() => summary.refetch()} />
      ) : (
        <Summary s={summary.data} />
      )}

      <div className="space-y-1 text-xs text-muted-foreground">
        {echo && <p>{windowText(echo.window)}</p>}
        {/* A login filter builds the row from that account alone, not
            everything the person holds. */}
        {filters.login && <p>Filtered by login: each row covers only the matched accounts, see Logins.</p>}
        {/* Some values can match while others don't: always say which. */}
        {missing && data?.data.length > 0 && <p className="text-amber-400">{missing}</p>}
      </div>

      {list.error ? (
        <ErrorBanner error={list.error} onRetry={() => list.refetch()} />
      ) : missing && data?.data.length === 0 ? (
        <p className="px-5 py-16 text-center text-sm text-muted-foreground">{missing}</p>
      ) : (
        <>
          <QueueTable
            columns={COLUMNS}
            isLoading={list.isLoading}
            rows={data?.data}
            renderRow={(row) => {
              const key = row.username ?? `login-${row.logins[0]}`;
              return (
                <ClientRows key={key} row={row} open={open.has(key)} onToggle={() => toggle(key)} />
              );
            }}
          />
          <PagePager data={data} onChange={setPage} />
        </>
      )}
    </QueueLayout>
  );
}
